import { CommonModule } from '@angular/common';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { Actions, Store, ofActionSuccessful } from '@ngxs/store';
import {
  CareManagementState,
  GetCurrentUserCalls,
  WrapUpCallLogging,
} from '@troyai/care-management/data-access';
import { PatientsState } from '@troyai/patients/data-access';
import {
  AircallCallEnded,
  AircallState,
  AircallTogglePanel,
  FinalizePostCallWrapUp,
} from '@troyai/portal/voice-calls/data-access';
import { TabSyncService } from '@troyai/shared/tab-sync';
import { IconsModule, ModalModule } from '@troyai/ui-kit';
import { ToastrModule, ToastrService } from 'ngx-toastr';
import {
  EMPTY,
  Subject,
  defer,
  expand,
  filter,
  map,
  switchMap,
  take,
  takeUntil,
  timer,
  withLatestFrom,
} from 'rxjs';
import { AircallService } from '../../aircall.service';

@Component({
  selector: 't-aircall-wrapper',
  templateUrl: './aircall-wrapper.component.html',
  imports: [CommonModule, IconsModule, ModalModule, ToastrModule],
})
export class AircallWrapperComponent implements OnInit, OnDestroy {
  private store = inject(Store);
  private aircallService = inject(AircallService);
  private actions$ = inject(Actions);
  private tabSyncService = inject(TabSyncService);
  private toastrService = inject(ToastrService);
  private destroy$ = new Subject<void>();

  @ViewChild('aircallWorkspace', { static: true })
  workspaceElement: ElementRef | undefined;

  isPanelOpen$ = this.store.select(AircallState.isPanelOpen);
  hasOngoingCall$ = this.store.select(AircallState.hasOngoingCall);
  currentCall$ = this.store.select(AircallState.currentCall);
  selectedPatient$ = this.store.select(PatientsState.selectedPatient);
  currentContext$ = this.store.select(AircallState.callIntentContext);
  callPhase$ = this.store.select(AircallState.callPhase);
  pendingCallsToLog$ = this.store.select(CareManagementState.pendingCallsToLog);

  ngOnInit() {
    const elementId = 'aircall-workspace-container';
    if (this.workspaceElement) {
      this.workspaceElement.nativeElement.id = elementId;
    }
    this.aircallService.init(`#${elementId}`);

    this.actions$
      .pipe(ofActionSuccessful(FinalizePostCallWrapUp), takeUntil(this.destroy$))
      .subscribe(() => {
        this.tabSyncService.broadcastMessage({
          type: 'refresh-cm-patients-listing',
          payload: {},
        });
      });

    this.actions$
      .pipe(ofActionSuccessful(WrapUpCallLogging), takeUntil(this.destroy$))
      .subscribe(() => {
        this.store.dispatch(new GetCurrentUserCalls({ filter: 'pendingLogging' }));
      });

    this.actions$
      .pipe(
        ofActionSuccessful(AircallCallEnded),
        withLatestFrom(this.selectedPatient$, this.pendingCallsToLog$),
        filter(([, patient]) => !!patient),
        switchMap(([, patient, initialPendingCalls]) => {
          const initialCount = initialPendingCalls?.result?.length ?? 0;
          const maxAttempts = 3;
          const retryDelay = 2000;

          const dispatchAndCheck$ = defer(() => {
            this.store.dispatch(new GetCurrentUserCalls({ filter: 'pendingLogging' }));
            return this.actions$.pipe(
              ofActionSuccessful(GetCurrentUserCalls),
              take(1),
              withLatestFrom(this.pendingCallsToLog$),
              map(([, calls]) => calls?.result?.length ?? 0)
            );
          });

          return dispatchAndCheck$.pipe(
            expand((count) => {
              if (count !== initialCount) {
                if (count > initialCount) {
                  this.toastrService.warning(
                    'The call is ready to be logged.',
                    `Outreach to ${patient?.first_name} ${patient?.last_name}`
                  );
                }
                return EMPTY;
              }
              return timer(retryDelay).pipe(switchMap(() => dispatchAndCheck$));
            }),
            take(maxAttempts)
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  ngOnDestroy() {
    this.aircallService.destroy();
    this.destroy$.next();
    this.destroy$.complete();
  }

  togglePanel() {
    this.store.dispatch(new AircallTogglePanel());
  }
}
